import { Dispatch, memo, useEffect, useState, VFC } from 'react';
import {
  EventContentArg,
  EventDropArg,
  FormatterInput
} from '@fullcalendar/core';
import { ColSpec } from '@fullcalendar/resource-common';

import FullCalendar from '@fullcalendar/react';
import jaLocale from '@fullcalendar/core/locales/ja';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin, {
  EventResizeDoneArg
} from '@fullcalendar/interaction';
import './style.css';
import { TGanttData } from '../../../../types/process';
import { getRokuyo } from '../../../../utils';
import { EventContent } from './EventContent';
import { useRecoilState, useRecoilValue } from 'recoil';
import { attendanceAssignTotallOfDate } from '../../../../GlobalState';

export type TProps = {
  calendarRef: any;
  eventStartEditable: boolean;
  eventDurationEditable: boolean;
  initialGanttData: TGanttData;
  initialResourceAreaColumns: Array<ColSpec>;
  initialResourceAreaWidth: string;
  handleEdit: (
    args: EventResizeDoneArg | EventDropArg,
    inputConstructId: number
  ) => void;
  constructId: number;
  setIsLoading: Dispatch<boolean>;
  initialVisibleRange: any;
};

export const AttendanceGanttChart: VFC<TProps> = memo(props => {
  const {
    calendarRef,
    eventStartEditable,
    eventDurationEditable,
    initialGanttData,
    initialResourceAreaColumns,
    initialResourceAreaWidth,
    handleEdit,
    constructId,
    setIsLoading,
    initialVisibleRange
  } = props;

  const assignTotallOfDate = useRecoilValue(attendanceAssignTotallOfDate);

  const slotLabelFormat: Array<FormatterInput> = [
    { year: 'numeric' },
    { month: 'long' },
    { day: 'numeric' },
    { weekday: 'short' },
    { weekday: 'short' }, // 六曜用
    { weekday: 'short' }
  ];

  return (
    <>
      <FullCalendar
        ref={calendarRef}
        // 日本語設定
        locales={[jaLocale]}
        locale='ja'
        // TODO:.envファイルにライセンスキーを設定する（https://fullcalendar.io/license）
        schedulerLicenseKey={process.env.REACT_APP_FULLCALENDAR_LICENCE_KEY}
        // ヘッダー＋フッター＋テーブルの高さ
        height={680}
        // 初期表示範囲
        visibleRange={initialVisibleRange}
        // plugin読み込み
        plugins={[resourceTimelinePlugin, interactionPlugin]}
        // 画面表示時の初期ビュー
        initialView={'resourceTimeline'}
        views={{
          resourceTimeline: {
            type: 'resourceTimeline',
            slotDuration: { days: 1 }
          }
        }}
        // 初期表示範囲
        // 編集権限
        eventStartEditable={eventStartEditable}
        eventDurationEditable={eventDurationEditable}
        eventResourceEditable={true}
        // インジケータの表示設定
        nowIndicator={true}
        // ヘッダ部分に表示する内容の制御
        headerToolbar={{
          left: '',
          center: '',
          right: ''
        }}
        // slotのラベル設定
        slotLabelFormat={slotLabelFormat}
        // slotラベルのスタイル設定
        slotLabelClassNames={args => {
          let styles = 'slot-style';
          if (args.level === 5) {
            styles = 'daily-report';
          }
          const weekday = args.date?.getDay();
          // 土日祝日のslotラベルの場合は色を変える
          if (args.level !== 1) {
            if (weekday === 0 || weekday === 6) {
              styles += ' holiday';
            }
          }
          return styles;
        }}
        // slotラベルのテキスト設定
        slotLabelContent={args => {
          // lebel0の年表示の年は消す（例：2023年→2023にする）
          if (args.level === 0) {
            return args.text.replace('年', '');
          }

          // level2の日表示の日は消す（例：17日→17にする）
          if (args.level === 2) {
            return args.text.replace('日', '');
          }
          // level3 土曜日と日曜日はカラーを変更
          if (args.level === 3) {
            const day = args.text;
            var colorStyle = 'general-day';
            if (day == '土') {
              colorStyle = 'saturday';
            } else if (day == '日') {
              colorStyle = 'sunday';
            }
            return <div className={colorStyle}>{args.text}</div>;
          }
          // 六曜名をいれる
          if (args.level === 4) {
            const rokuyo = getRokuyo(
              args.date.getFullYear(),
              args.date.getMonth() + 1,
              args.date.getDate()
            );
            var colorStyle = 'general-day';
            if (rokuyo == '大安') {
              colorStyle = 'lucky-day';
            }

            return <div className={colorStyle}>{rokuyo}</div>;
          }
          // 集計
          if (args.level === 5) {
            const date = args.date;
            const year = date.getFullYear();
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            const formattedDate = `${year}-${month}-${day}`;

            let count = 0;

            if (assignTotallOfDate && assignTotallOfDate.length > 0) {
              assignTotallOfDate.filter((i: any) => {
                if (i.target_date == formattedDate) {
                  count = i.artificial;
                }
              });
            }
            return <div>{count}</div>;
          }
          return args.text;
        }}
        // slotのスタイル設定
        slotLaneClassNames={args => {
          let styles = 'slot-style';
          // 土日祝日のslotセルは色を変える
          const weekday = args.date?.getDay();
          if (weekday === 0 || weekday === 6) {
            styles += ' holiday';
          }

          return styles;
        }}
        resourceOrder={(resource1: any, resource2: any) => {
          if (resource1.constructOrder < resource2.constructOrder) {
            return -1;
          } else if (resource1.constructOrder > resource2.constructOrder) {
            return 1;
          }

          if (resource1.order < resource2.order) {
            return -1;
          } else if (resource1.order > resource2.order) {
            return 1;
          }

          if (resource1.id < resource2.id) {
            return -1;
          } else if (resource1.id > resource2.id) {
            return 1;
          }

          return 0;
        }}
        // resourceの幅
        resourceAreaWidth={initialResourceAreaWidth}
        // resourceを初回に折りたたむかどうかの設定
        // resourcesInitiallyExpanded={false}
        // リソースに表示するデータの制御
        resourceAreaColumns={initialResourceAreaColumns}
        // イベントの表示順
        eventOrder={(event1: any, event2: any) => {
          if (event1.priorityOrder < event2.priorityOrder) {
            return -1;
          } else if (event1.priorityOrder > event2.priorityOrder) {
            return 1;
          }
          if (event1.order < event2.order) {
            return -1;
          } else if (event1.order > event2.order) {
            return 1;
          }
          if (event1.start < event2.start) {
            return -1;
          } else if (event1.start > event2.start) {
            return 1;
          }
          if (event1.end < event2.end) {
            return -1;
          } else if (event1.end > event2.end) {
            return 1;
          }

          return 0;
        }}
        // イベントのstyle
        eventClassNames={['event-style']}
        // イベントに表示するデータの制御
        eventContent={(eventContent: EventContentArg) => {
          return (
            <EventContent
              eventContent={eventContent}
              constructId={constructId}
              setIsLoading={setIsLoading}
            />
          );
        }}
        //resourceデータ（大項目）
        resources={initialGanttData?.resources}
        // eventデータ（小項目）
        events={initialGanttData?.events}
        // 工程のリサイズ
        eventResize={args => {
          handleEdit(args, constructId);
        }}
        // 工程の移動
        eventDrop={args => {
          handleEdit(args, constructId);
        }}
      />
    </>
  );
});
